// Client/src/components/Sidebar.tsx
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  AlertTriangle,
  Award,
  BarChart2,
  BookOpen,
  CalendarClock,
  Building,
  ChevronDown,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Gauge,
  History,
  Image,
  ListChecks,
  LogOut,
  Mail,
  MessageSquare,
  Package,
  PackageSearch,
  ScanLine,
  Server,
  Settings,
  Sparkles,
  Ticket,
  Timer,
  TrendingUp,
  Truck,
  UserCircle,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { fetchWithAuth } from '../store/fetchClient';
import type { UserRole } from '../types';
import GuestQrLauncher from './GuestQrLauncher';
import { isNativeShellMode } from '../utils/nativeClientEnv';

interface SidebarProps {
  sidebarOpen: boolean;
  closeSidebar: () => void;
}

type Permission = 'manageUsers' | 'manageStores' | 'createTask';
type UserPermission = 'shelf_action_link_reports';

interface SidebarItem {
  to: string;
  icon: LucideIcon;
  label: string;
  permission?: Permission;
  userPermission?: UserPermission;
  mobileVisible?: boolean;
  roles?: readonly UserRole[];
  requireRevision?: boolean;
}

interface SidebarSectionConfig {
  id: string;
  title?: string;
  items: SidebarItem[];
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const ADMIN_ROLES = ['admin'] as const;
const MARKET_LEAD_ROLES = ['admin', 'filialleiter', 'vertretenderfilialleiter'] as const;
const USER_MANAGEMENT_ROLES = ['admin', 'filialleiter', 'manager_reinigungskraft', 'dm_Reinigung'] as const;
const GUEST_QR_ALLOWED_ROLES = ['tablet', 'filialleiter', 'vertretenderfilialleiter', 'admin'] as const;

const WORK_ITEMS: SidebarItem[] = [
  { to: '/', icon: BarChart2, label: 'Dashboard' },
  { to: '/tasks', icon: ListChecks, label: 'Aufgaben' },
  { to: '/marktplan', icon: CalendarClock, label: 'Marktplan' },
  { to: '/tickets', icon: Ticket, label: 'Tickets' },
  { to: '/katalog', icon: BookOpen, label: 'Katalog' },
  { to: '/hausmeister', icon: Wrench, label: 'Hausmeisterdienst' },
  { to: '/reinigung', icon: Sparkles, label: 'Reinigungsplan' },
  { to: '/rolli', icon: Archive, label: 'Defektur' },
  { to: '/we-check', icon: PackageSearch, label: 'WE-Check' },
  { to: '/formular-uebersicht', icon: ClipboardList, label: 'Ersatzteilbestellungen' },
  { to: '/shelf/layouts', icon: Package, label: 'Zweitplatzierungslayouts' },
  { to: '/shelf/actions', icon: Package, label: 'Zweitplatzierungsaktionen' },
  { to: '/zeiterfassung', icon: Timer, label: 'Zeiterfassung' },
];

const ANALYSIS_ITEMS: SidebarItem[] = [
  { to: '/nachweise', icon: FileText, label: 'Nachweise' },
  { to: '/admin/wareneingang', icon: Truck, label: 'Wareneingang', mobileVisible: false, roles: ADMIN_ROLES },
  { to: '/admin/mhd', icon: CalendarClock, label: 'MHD-Auswertung', mobileVisible: false },
  { to: '/zeiterfassung-analyse', icon: TrendingUp, label: 'Zeiterfassung Analyse', mobileVisible: false },
  {
    to: '/revisions/auswertung',
    icon: TrendingUp,
    label: 'Revision Auswertung',
    mobileVisible: false,
    roles: MARKET_LEAD_ROLES,
    requireRevision: true,
  },
];

const HISTORY_ITEMS: SidebarItem[] = [
  { to: '/verlauf', icon: History, label: 'Aufgabenverlauf' },
  { to: '/export', icon: Download, label: 'Export' },
  { to: '/planner', icon: Download, label: 'Planansicht', mobileVisible: false },
];

const MANAGEMENT_ITEMS: SidebarItem[] = [
  { to: '/stores', icon: Building, label: 'Märkte' },
  { to: '/users', icon: Users, label: 'Benutzer', roles: USER_MANAGEMENT_ROLES },
  { to: '/shelf/action-link-reports', icon: AlertTriangle, label: 'Aktionslink-Meldungen', userPermission: 'shelf_action_link_reports' },
  { to: '/gastgeraete', icon: Users, label: 'Gastgeräte', roles: MARKET_LEAD_ROLES },
  { to: '/bildpool', icon: Image, label: 'Bildpool' },
  { to: '/kampagnen', icon: Award, label: 'Kampagnen' },
  { to: '/mitteilungen', icon: MessageSquare, label: 'Mitteilungen', roles: MARKET_LEAD_ROLES },
];

const SETTINGS_ITEMS: SidebarItem[] = [
  { to: '/layoutbaustein', icon: Archive, label: 'Layoutbereiche', roles: ADMIN_ROLES },
  { to: '/dm-kategorien', icon: Package, label: 'OnlineShop', roles: ADMIN_ROLES },
  { to: '/sperrtage', icon: Settings, label: 'Sperrtage', roles: ['admin', 'filialleiter'] as const },
  { to: '/tages-kapazitaeten', icon: Gauge, label: 'Tages-Kapazitäten', roles: ['admin', 'filialleiter'] as const },
  { to: '/anlieferplan', icon: Truck, label: 'Anlieferplan', roles: ADMIN_ROLES },
  { to: '/admin/shelfs', icon: Package, label: 'Zweitplatzierungsmöbel', roles: ADMIN_ROLES },
  { to: '/admin/dm-artikel', icon: Package, label: 'DM Artikel', roles: ADMIN_ROLES },
  { to: '/admin/mhd-einstellungen', icon: CalendarClock, label: 'MHD-Einstellungen', roles: ADMIN_ROLES },
  { to: '/client-status', icon: Server, label: 'Client Status', roles: ADMIN_ROLES },
  { to: '/scanner-test', icon: ScanLine, label: 'Scanner-Test', roles: ADMIN_ROLES },
  { to: '/admin-system', icon: Server, label: 'Admin System', roles: ADMIN_ROLES },
  { to: '/einstellungen/formular-templates', icon: ClipboardList, label: 'Formular-Templates', roles: ADMIN_ROLES },
  { to: '/einstellungen/rolli-zeitvorgaben', icon: TrendingUp, label: 'Rolli Zeitvorgaben', roles: ADMIN_ROLES },
  { to: '/einstellungen/zeitkategorien', icon: Clock, label: 'Zeitkategorien', roles: ADMIN_ROLES },
  { to: '/settings/secondary-placement-reports', icon: Mail, label: 'Zweitplatzierungsberichte', roles: ADMIN_ROLES },
  { to: '/settings', icon: Mail, label: 'System & E-Mail-Queue', roles: ADMIN_ROLES },
  { to: '/revisions/admin', icon: Settings, label: 'Revision Felder', roles: ADMIN_ROLES },
];

const LAYOUT_DESIGNER_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'layoutdesigner',
    items: [
      { to: '/shelf/layouts', icon: Package, label: 'Zweitplatzierungslayouts' },
      { to: '/admin/shelfs', icon: Package, label: 'Zweitplatzierungsmöbel' },
    ],
  },
];

const CLEANING_STAFF_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'cleaning',
    items: [{ to: '/reinigung', icon: Sparkles, label: 'Reinigungsplan' }],
  },
];

const CLEANING_MANAGER_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'cleaning-management',
    title: 'Reinigung',
    items: [
      { to: '/reinigung/uebersicht', icon: Sparkles, label: 'Reinigungsübersicht' },
      { to: '/reinigung', icon: Sparkles, label: 'Reinigungsplan' },
      { to: '/users', icon: Users, label: 'Benutzer', roles: USER_MANAGEMENT_ROLES },
    ],
  },
];

const DEFAULT_SECTIONS: SidebarSectionConfig[] = [
  { id: 'work', title: 'Arbeit', items: WORK_ITEMS },
  { id: 'analysis', title: 'Auswertung', items: ANALYSIS_ITEMS },
  { id: 'history', title: 'Verlauf', items: HISTORY_ITEMS },
  { id: 'management', title: 'Verwaltung', items: MANAGEMENT_ITEMS, collapsible: true },
  { id: 'settings', title: 'Einstellungen', items: SETTINGS_ITEMS, collapsible: true },
];

function isPathActive(pathname: string, to: string) {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function hasRole(role: UserRole | undefined, roles?: readonly UserRole[]) {
  return !roles || (!!role && roles.includes(role));
}

function itemIsVisible(
  item: SidebarItem,
  opts: {
    role: UserRole | undefined;
    isMobile: boolean;
    revisionsEnabled: number;
    hasPermission: (permission: Permission) => boolean;
    userPermissions: string[];
  }
) {
  if (!hasRole(opts.role, item.roles)) return false;
  if (item.requireRevision && opts.revisionsEnabled <= 0) return false;
  if (item.permission && !opts.hasPermission(item.permission)) return false;
  if (item.userPermission && opts.role !== 'admin' && !(opts.userPermissions || []).includes(item.userPermission)) return false;
  return !opts.isMobile || item.mobileVisible !== false;
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    'group flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
    isActive
      ? 'bg-indigo-50 text-indigo-700 font-semibold'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  ].join(' ');
}

function SidebarNavItem({ item, closeSidebar }: { item: SidebarItem; closeSidebar: () => void }) {
  const Icon = item.icon;

  return (
    <NavLink to={item.to} onClick={closeSidebar} className={navLinkClass}>
      <Icon className="h-5 w-5 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarSection({
  section,
  closeSidebar,
  pathname,
  open,
  onToggle,
}: {
  section: SidebarSectionConfig;
  closeSidebar: () => void;
  pathname: string;
  open?: boolean;
  onToggle?: () => void;
}) {
  if (section.items.length === 0) return null;

  const sectionIsActive = section.items.some((item) => isPathActive(pathname, item.to));
  const isOpen = !section.collapsible || open || sectionIsActive;

  return (
    <div className={section.title ? 'space-y-1' : 'space-y-1 pt-1'}>
      {section.title && (
        section.collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            className={[
              'flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors',
              sectionIsActive ? 'text-indigo-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
            ].join(' ')}
          >
            <span className="truncate">{section.title}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        ) : (
          <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            {section.title}
          </p>
        )
      )}

      {isOpen && (
        <div className="space-y-1">
          {section.items.map((item) => (
            <SidebarNavItem key={item.to} item={item} closeSidebar={closeSidebar} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ sidebarOpen, closeSidebar }: SidebarProps) {
  const { hasPermission, isCleaningStaff, isCleaningManager, logout, user, selectedStoreId } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    management: false,
    settings: false,
  });
  const [revisionsEnabled, setRevisionsEnabled] = useState(0);
  const role = user?.role as UserRole | undefined;
  const isLayoutDesigner = role === 'layoutdesigner';
  const isTabletUser = role === 'tablet' || isNativeShellMode();
  const canLaunchGuestQr = Boolean(role && GUEST_QR_ALLOWED_ROLES.includes(role as (typeof GUEST_QR_ALLOWED_ROLES)[number]));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadRevisionSetting = async () => {
      if (!selectedStoreId) {
        setRevisionsEnabled(0);
        return;
      }

      try {
        const response = await fetchWithAuth(`/api/stores/${selectedStoreId}/settings/revisionsEnabled`);
        if (!response.ok) {
          setRevisionsEnabled(0);
          return;
        }
        const data = await response.json();
        setRevisionsEnabled(Number(data.revisionsEnabled ?? 0));
      } catch {
        setRevisionsEnabled(0);
      }
    };

    void loadRevisionSetting();
  }, [selectedStoreId]);

  const visibleSections = useMemo(() => {
    const sourceSections = isLayoutDesigner
      ? LAYOUT_DESIGNER_SECTIONS
      : isCleaningStaff()
        ? CLEANING_STAFF_SECTIONS
        : isCleaningManager()
          ? CLEANING_MANAGER_SECTIONS
          : DEFAULT_SECTIONS;

    return sourceSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          itemIsVisible(item, {
            role,
            isMobile,
            revisionsEnabled,
            hasPermission,
            userPermissions: user?.permissions || [],
          })
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [hasPermission, isCleaningManager, isCleaningStaff, isLayoutDesigner, isMobile, revisionsEnabled, role, user?.permissions]);

  useEffect(() => {
    const activeSection = visibleSections.find((section) =>
      section.collapsible && section.items.some((item) => isPathActive(location.pathname, item.to))
    );

    if (activeSection) {
      setOpenSections((prev) => ({ ...prev, [activeSection.id]: true }));
    }
  }, [location.pathname, visibleSections]);

  const handleLogout = async () => {
    if (!window.confirm('Möchten Sie sich wirklich abmelden?')) return;

    try {
      await logout();
    } catch (err) {
      console.error('Logout fehlgeschlagen:', err);
    } finally {
      closeSidebar();
      navigate('/');
    }
  };

  const sidebarClasses = `
    fixed left-0 top-16 z-30 w-64 bg-white shadow-lg
    transform transition-transform duration-300
    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
    lg:static lg:block lg:translate-x-0
  `;

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={closeSidebar}
          aria-hidden
        />
      )}

      <aside
        className={sidebarClasses}
        style={{
          height: 'calc(var(--app-window-height) - 4rem)',
          boxSizing: 'border-box',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <button
          onClick={closeSidebar}
          className="absolute right-3 top-3 rounded-md p-2 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:hidden"
          aria-label="Sidebar schließen"
          type="button"
        >
          <X className="h-5 w-5" />
        </button>

        <nav className="flex h-full flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-4 pt-5">
            {visibleSections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                closeSidebar={closeSidebar}
                pathname={location.pathname}
                open={openSections[section.id] ?? section.defaultOpen ?? false}
                onToggle={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    [section.id]: !(prev[section.id] ?? section.defaultOpen ?? false),
                  }))
                }
              />
            ))}

            {canLaunchGuestQr && !isLayoutDesigner && !isCleaningStaff() && !isCleaningManager() && (
              <div className="border-t border-slate-200 pt-3">
                <GuestQrLauncher
                  selectedStoreId={selectedStoreId}
                  onTrigger={closeSidebar}
                  className="flex w-full items-center gap-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-3">
            {!isTabletUser && (
              <NavLink to="/profil" onClick={closeSidebar} className={navLinkClass}>
                <UserCircle className="h-5 w-5 shrink-0" />
                <span className="truncate">Mein Profil</span>
              </NavLink>
            )}
            <button
              onClick={handleLogout}
              className="mt-1 flex w-full min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              type="button"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="truncate">Abmelden</span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
}
